/**
 * setsDiscovered — per-swimmer ledger of named swim sets.
 *
 * Pure-function core (Tasks 4–8). No Svelte runtime here — all store wiring
 * lives in Phase C (Task 9). All I/O is injected so every function is
 * unit-testable without a bridge or HealthKit.
 */

import { writable, get } from "svelte/store";
import { SET_DISPLAY } from "./setLibrary";
import { deriveWorkout, type DerivedWorkout } from "./lapAnalysis";
import { recordStrokeStats, hasStrokeStats } from "./strokeStats";
import { recordSwimFacts, hasSwimFacts } from "./allTimeAggregates";
import { currentRawFirehose, currentDerivedWorkout } from "./state";
import { bridge } from "../bridge/client";
import { loadSavedLabels } from "./savedLabels";
import type { WorkoutSummary, SwimFirehoseResponse, LapLabel } from "@contract/types";

// ─── Cache model ─────────────────────────────────────────────────────────────

/**
 * Cache version. Bumped 2→3 on 2026-09-09 for the "a known set trumps
 * Warmup/Cooldown" label rule (setGroups): the ledger counts each swim ONCE and
 * never revisits it, so a label rule change is a CACHE MIGRATION — without the
 * bump the new rule never reaches a swim already counted, and the ribbon would
 * read "The Rockalita" while the ledger held "cooldown" for that swim forever.
 * The KEY still says v2 (the v1→v2 bump changed the key instead); from v3 on
 * the `version` field is the authority, which also lets loadCache read the
 * outgoing blob straight out of the rejected parse.
 */
export const CACHE_VERSION = 3;

export interface SetsDiscoveredEntry {
  setId: string;
  count: number;
  firstSwumDate: number; // epoch ms
  lastSwumDate: number; // epoch ms
  firstSwumWorkoutId: string;
}

export interface SetsDiscoveredCache {
  version: number;
  builtAt: number; // epoch ms of last scan; 0 when never scanned
  appliedWorkoutIds: string[]; // gates COUNTING (idempotency)
  shownCallouts: string[]; // gates RECOGNITION (one callout per swim)
  entries: Record<string, SetsDiscoveredEntry>;
  /**
   * The pre-migration ledger, present ONLY between a version bump and the
   * recount that follows it. Two jobs, then `settleCarried` drops it:
   *
   *  - PIN first-seen. `firstEverForShownSwim` keys the one-time "first ever"
   *    callout off `firstSwumWorkoutId`, so a recount that moved a set's
   *    first-seen to an older swim would fire a callout the user never asked
   *    to see again. The pinned dates are the ones already on screen.
   *  - FLOOR the counts. The scan reads `listWorkouts({ limit: 100 })`, so a
   *    set whose swims are ALL older than the 100 most recent would recount to
   *    nothing — the exact regression the v1→v2 carry-over was written to
   *    prevent. Anything the recount never reaches keeps its old row.
   *
   * Absent on a settled cache, so `saveCache` never persists it twice.
   */
  carried?: Record<string, SetsDiscoveredEntry>;
}

export interface AppliedSwim {
  uuid: string;
  startedAtMs: number;
  setIds: (string | null)[];
}

export function emptyCache(): SetsDiscoveredCache {
  return {
    version: CACHE_VERSION,
    builtAt: 0,
    appliedWorkoutIds: [],
    shownCallouts: [],
    entries: {},
  };
}

// ─── Task 4: applySwimToCache ────────────────────────────────────────────────

/** Pure. Returns a new cache with this swim folded in (idempotent by uuid). */
export function applySwimToCache(
  cache: SetsDiscoveredCache,
  swim: AppliedSwim,
): SetsDiscoveredCache {
  if (cache.appliedWorkoutIds.includes(swim.uuid)) return cache;

  let ids = [...new Set(swim.setIds.filter((id): id is string => id !== null))];
  if (ids.length === 0) ids = ["warmup"]; // D4 first-swim guarantee

  const entries = { ...cache.entries };
  for (const id of ids) {
    // A migration recount must not move a first-seen the user has already been
    // shown — see SetsDiscoveredCache.carried. No migration in flight, no pin,
    // and this is exactly today's behavior.
    const pin = cache.carried?.[id];
    const prev = entries[id];
    if (!prev) {
      entries[id] = {
        setId: id,
        count: 1,
        firstSwumDate: pin?.firstSwumDate ?? swim.startedAtMs,
        lastSwumDate: Math.max(pin?.lastSwumDate ?? 0, swim.startedAtMs),
        firstSwumWorkoutId: pin?.firstSwumWorkoutId ?? swim.uuid,
      };
    } else {
      const earliest = !pin && swim.startedAtMs < prev.firstSwumDate;
      entries[id] = {
        ...prev,
        count: prev.count + 1,
        firstSwumDate: pin ? pin.firstSwumDate : Math.min(prev.firstSwumDate, swim.startedAtMs),
        lastSwumDate: Math.max(prev.lastSwumDate, swim.startedAtMs),
        firstSwumWorkoutId: pin
          ? pin.firstSwumWorkoutId
          : earliest
            ? swim.uuid
            : prev.firstSwumWorkoutId,
      };
    }
  }
  return { ...cache, entries, appliedWorkoutIds: [...cache.appliedWorkoutIds, swim.uuid] };
}

// ─── Task 5: Recognition ─────────────────────────────────────────────────────

/** setIds whose earliest-ever swim IS this one, unless its callout already showed. */
export function firstEverForShownSwim(cache: SetsDiscoveredCache, uuid: string): string[] {
  if (cache.shownCallouts.includes(uuid)) return [];
  return Object.values(cache.entries)
    .filter((e) => e.firstSwumWorkoutId === uuid)
    .map((e) => e.setId);
}

export function markCalloutShown(cache: SetsDiscoveredCache, uuid: string): SetsDiscoveredCache {
  if (cache.shownCallouts.includes(uuid)) return cache;
  return { ...cache, shownCallouts: [...cache.shownCallouts, uuid] };
}

// Names that are already a full phrase starting with "The " ("The
// Rockalita") collide with the "First " superlative ("First The Rockalita
// we've tracked") -- pull the article to the front instead: "The first
// Rockalita we've tracked." Mirrors the peakSetPhrase fix in
// SwimHeroCard.svelte (persona-gate fix, W1 re-gate).
export function firstEverSentence(names: string[]): string {
  const list =
    names.length === 1
      ? names[0]
      : names.length === 2
        ? `${names[0]} and ${names[1]}`
        : `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
  return list.startsWith("The ")
    ? `The first ${list.slice(4)} we've tracked.`
    : `First ${list} we've tracked.`;
}

// ─── Task 6: Ledger rows ──────────────────────────────────────────────────────

export interface LedgerRow {
  setId: string;
  displayName: string;
  plainDefinition: string;
  isSignature: boolean;
  count: number;
  firstSwumDate: number;
  lastSwumDate: number;
  firstSwumWorkoutId: string;
}

/**
 * Warmup & Cooldown are bookends, not "sets" (Scott's rule). They are the least
 * set-like (every swim has them; a 1-2 chip ribbon next to "100 swims" reads as
 * a render failure). Suppressed from BOTH the rendered ledger and the first-time
 * recognition callout. Revert by emptying this set. (Blind-review BLOCKER, 2026-06.)
 */
export const BOOKEND_SET_IDS = new Set(["warmup", "cooldown"]);

export function toLedgerRows(cache: SetsDiscoveredCache): LedgerRow[] {
  return Object.values(cache.entries)
    .filter((e) => SET_DISPLAY[e.setId] !== undefined)
    .filter((e) => !BOOKEND_SET_IDS.has(e.setId))
    // Generic single-stroke sets (Freestyle set / Stroke Set, isSignature:false)
    // ARE listed since 2026-09-01: the Scores "Your sets" section ranks every
    // named set by times swum (spec item 3 names the Freestyle set explicitly),
    // and its "All N sets you've swum" row must open a sheet with the same N.
    // They were dropped before because a 1-color chip ribbon read as a render
    // failure next to the named gems — the sheet now skips the ribbon for them
    // and shows their times instead. First-ever recognition still ignores them
    // (processCurrentSwim filters on isSignature separately).
    .map((e) => {
      const d = SET_DISPLAY[e.setId];
      return {
        setId: e.setId,
        displayName: d.displayName,
        plainDefinition: d.plainDefinition,
        isSignature: d.isSignature,
        count: e.count,
        firstSwumDate: e.firstSwumDate,
        lastSwumDate: e.lastSwumDate,
        firstSwumWorkoutId: e.firstSwumWorkoutId,
      };
    })
    .sort((a, b) => b.lastSwumDate - a.lastSwumDate);
}

// ─── Ledger copy ─────────────────────────────────────────────────────────────

/** "June 2026" — the ledger rows' format. */
export function fmtMonthYear(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
}

// ─── Task 7: localStorage persistence ────────────────────────────────────────

export const CACHE_KEY = "swimset.setsDiscovered.v2";

/**
 * Key the v1 cache lived under before the v1→v2 bump (f3dfe22). The bump
 * changed the KEY itself (not an in-place version field), so a v2 loadCache()
 * never naturally sees the old blob — see salvageShownCallouts below.
 */
export const CACHE_KEY_V1 = "swimset.setsDiscovered.v1";

/**
 * Best-effort recovery of shownCallouts (the first-ever recognition ledger)
 * from the pre-bump v1 blob. entries/appliedWorkoutIds are safely re-derived
 * by the backfill scan, but shownCallouts is not — without this, a swim whose
 * callout already fired pre-migration can re-fire it once, if the live
 * subscription reprocesses that swim before the scan rebuilds entries.
 * Never throws: a missing or corrupt v1 blob just yields no salvage.
 */
function salvageShownCallouts(): string[] {
  try {
    const raw = typeof localStorage === "undefined" ? null : localStorage.getItem(CACHE_KEY_V1);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { shownCallouts?: unknown };
    if (!Array.isArray(parsed.shownCallouts)) return [];
    return parsed.shownCallouts.filter((id): id is string => typeof id === "string");
  } catch {
    return [];
  }
}

/**
 * Union `raw` (an untrusted parsed shownCallouts field) with `salvaged`,
 * deduped. H4 fix (wave-1 cross-vendor review): a structurally-valid v2 blob
 * previously used ITS shownCallouts only, on every load — dropping any
 * callout the pre-migration v1 blob still held (dual-key precedence gap:
 * partial migration, manual edit, or a pre-salvage build that wrote v2 with
 * an empty shownCallouts while v1 still had UUIDs). `Array.isArray` guards
 * against a malformed `raw` (e.g. `shownCallouts: false`) that would
 * otherwise throw on spread — same laxness M1 flagged in the old `?? []`.
 */
function unionShownCallouts(raw: unknown, salvaged: string[]): string[] {
  const fromV2 = Array.isArray(raw)
    ? raw.filter((id): id is string => typeof id === "string")
    : [];
  return [...new Set([...fromV2, ...salvaged])];
}

/**
 * Best-effort recovery of an OLDER blob's `entries`, as the migration snapshot
 * (SetsDiscoveredCache.carried). Entries only — never `appliedWorkoutIds`.
 *
 * The v1→v2 migration carried appliedWorkoutIds over VERBATIM (Codex-High,
 * wave-1 cross-vendor review) because that bump kept the carried COUNTS, and
 * entries store only an aggregate `count` with no per-uuid contribution record:
 * carrying counts while resetting appliedWorkoutIds double-counts every swim
 * that is both already-counted and inside the re-scan window.
 *
 * v3 inverts that trade and so must NOT carry appliedWorkoutIds. A label rule
 * change makes the carried counts themselves wrong, so v3 recounts from zero —
 * and a carried appliedWorkoutIds list would re-block exactly the swims the
 * recount exists to revisit. There is no double-count to guard against,
 * because there are no carried counts to double.
 *
 * What that trade cost in v1→v2 is paid back by `carried` instead: the
 * snapshot pins first-seen so no recognition callout re-fires, and floors any
 * set the rescan cannot reach (its swims older than `listWorkouts`' 100-swim
 * window) so no count regresses to nothing. See `settleCarried`.
 *
 * Never throws: a missing or corrupt blob yields nothing to carry over.
 */
function salvageEntries(key: string): Record<string, SetsDiscoveredEntry> | null {
  try {
    const raw = typeof localStorage === "undefined" ? null : localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { entries?: unknown };
    if (typeof parsed.entries !== "object" || parsed.entries === null) return null;
    return parsed.entries as Record<string, SetsDiscoveredEntry>;
  } catch {
    return null;
  }
}

/**
 * A cache for the pre-v3 world: everything irreversible survives, everything
 * countable is rebuilt. `outgoing` is the rejected blob when loadCache still
 * holds it (a version mismatch parses before it rejects), null otherwise.
 */
function migratedCache(outgoing: Partial<SetsDiscoveredCache> | null): SetsDiscoveredCache {
  const carried = {
    ...(salvageEntries(CACHE_KEY_V1) ?? {}),
    ...(outgoing?.entries ?? salvageEntries(CACHE_KEY) ?? {}),
  };
  return {
    ...emptyCache(),
    shownCallouts: unionShownCallouts(outgoing?.shownCallouts, salvageShownCallouts()),
    ...(Object.keys(carried).length > 0 ? { carried } : {}),
  };
}

/**
 * End of a migration recount: restore any set the rescan could not reach, then
 * spend the snapshot. Idempotent — a settled cache (no `carried`) is returned
 * unchanged, so calling it on every scan is safe.
 */
export function settleCarried(cache: SetsDiscoveredCache): SetsDiscoveredCache {
  if (!cache.carried) return cache;
  const entries = { ...cache.entries };
  for (const [id, entry] of Object.entries(cache.carried)) {
    if (!entries[id]) entries[id] = entry;
  }
  const settled = { ...cache, entries };
  delete settled.carried;
  return settled;
}

export function loadCache(): SetsDiscoveredCache {
  try {
    const raw = typeof localStorage === "undefined" ? null : localStorage.getItem(CACHE_KEY);
    // No blob at all: nothing to reject, but an older key may still hold a
    // ledger worth carrying (shownCallouts, and the migration snapshot).
    if (!raw) return migratedCache(null);

    const parsed = JSON.parse(raw) as Partial<SetsDiscoveredCache>;
    if (
      parsed.version !== CACHE_VERSION ||
      typeof parsed.entries !== "object" ||
      parsed.entries === null
    ) {
      // The pre-v3 blob is right here in `parsed` — hand it to the migration
      // rather than re-reading it. H3/H4: whatever shownCallouts this rejected
      // blob still carries are unioned in, never discarded.
      return migratedCache(parsed);
    }

    return {
      version: CACHE_VERSION,
      builtAt: parsed.builtAt ?? 0,
      appliedWorkoutIds: parsed.appliedWorkoutIds ?? [],
      // H4 fix: a structurally-valid blob previously used ITS shownCallouts
      // only, never merging the pre-migration v1 blob still sitting on disk —
      // union both, idempotent and cheap on every load.
      shownCallouts: unionShownCallouts(parsed.shownCallouts, salvageShownCallouts()),
      entries: parsed.entries as Record<string, SetsDiscoveredEntry>,
      // `carried` is persisted DELIBERATELY while a migration is in flight, so
      // an app killed mid-recount resumes with first-seen still pinned and the
      // floor still available. settleCarried spends it when the scan finishes.
      ...(parsed.carried ? { carried: parsed.carried } : {}),
    };
  } catch {
    // H3 fix: corrupt/truncated JSON must not silently drop the recognition
    // ledger — migrate here too, not just on the missing/wrong-version
    // branches above. salvageShownCallouts / salvageEntries never throw
    // (their own try/catch), so this stays safe even if localStorage itself
    // is misbehaving.
    return migratedCache(null);
  }
}

export function saveCache(cache: SetsDiscoveredCache): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* quota / private-mode / purged — ledger silently degrades to in-session only (known debt) */
  }
}

// ─── Task 8: History scan ─────────────────────────────────────────────────────

export interface ScanDeps {
  cache: SetsDiscoveredCache;
  listWorkouts: () => Promise<WorkoutSummary[]>;
  getFirehose: (uuid: string) => Promise<SwimFirehoseResponse | null>;
  setIdsFor: (fh: SwimFirehoseResponse, labels: LapLabel[]) => (string | null)[];
  /**
   * The swim's saved stroke corrections (savedLabels.loadSavedLabels in the
   * app). Read once per swim and used for BOTH the counted setIds and the
   * onDerived side-write, so the ledger, the Scores facts and the Swim tab
   * agree about a corrected swim. Optional: omitted = no corrections.
   */
  labelsFor?: (uuid: string) => Promise<LapLabel[]>;
  /**
   * Optional side-write callback invoked AFTER each successfully-fetched
   * firehose is folded into the cache. Lets a SECONDARY cache (strokeStats.v2)
   * piggyback on this scan's firehose WITHOUT a second N-firehose fetch.
   * Receives the workout derived with the swim's saved labels. Optional so
   * existing callers/tests are unaffected; NEVER writes to the setsDiscovered cache.
   */
  onDerived?: (derived: DerivedWorkout, swimUuid: string, swimDate: string) => void;
  /**
   * Optional: does the onDerived side-write already have data for this uuid?
   * Codex-Medium fix (wave-1 cross-vendor review): applySwimToCache marks a
   * workout applied BEFORE onDerived runs, so a deriveWorkout/
   * recordStrokeStats failure used to strand that swim forever — once
   * counted, it's excluded from every future scan's `workouts` list and
   * onDerived never gets a second chance.
   *
   * When provided, a workout already counted (in appliedWorkoutIds) but
   * whose hasDerived check says false is refetched and retried WITHOUT
   * re-counting it (see `needsRetryDerive` below) — counting stays
   * idempotent (applySwimToCache's own uuid guard), only the side-write
   * retries. Also the mechanism the Codex-High carry-over fix (salvageV1Ledger
   * in loadCache) relies on to still backfill strokeStats for carried-over
   * history that skipped the reset-appliedWorkoutIds re-scan. Omit for
   * existing callers/tests — matches today's behavior (no retry) when unset.
   */
  hasDerived?: (uuid: string) => boolean;
}

export async function scanHistory(deps: ScanDeps): Promise<SetsDiscoveredCache> {
  let cache = deps.cache;
  const needsRetryDerive = (uuid: string): boolean =>
    !!deps.onDerived &&
    !!deps.hasDerived &&
    cache.appliedWorkoutIds.includes(uuid) &&
    !deps.hasDerived(uuid);

  const workouts = (await deps.listWorkouts())
    .filter((w) => w.activityType === "swimming")
    .filter((w) => !cache.appliedWorkoutIds.includes(w.uuid) || needsRetryDerive(w.uuid));

  for (const w of workouts) {
    let fh: SwimFirehoseResponse | null;
    try {
      fh = await deps.getFirehose(w.uuid);
    } catch {
      fh = null;
    }
    if (!fh) {
      console.warn(`[setsDiscovered] skipping ${w.uuid}: no firehose`);
      continue;
    }
    // Counting stays idempotent by uuid — a workout pulled back in ONLY to
    // retry a failed/missing onDerived side-write must not be re-applied to
    // the counting ledger a second time (Codex-Medium / Codex-High).
    // One label read per swim, shared by the count and the side-write.
    const labels = deps.labelsFor ? await deps.labelsFor(w.uuid) : [];
    if (!cache.appliedWorkoutIds.includes(w.uuid)) {
      cache = applySwimToCache(cache, {
        uuid: w.uuid,
        startedAtMs: new Date(w.startDate).getTime(),
        setIds: deps.setIdsFor(fh, labels),
      });
    }
    // Write path C: hand the already-fetched firehose to the optional secondary
    // side-write. Derived here (pure, NO second fetch); a failure never aborts
    // set discovery. Does NOT touch the setsDiscovered cache.
    if (deps.onDerived) {
      try {
        const derived = deriveWorkout(fh, labels);
        deps.onDerived(derived, w.uuid, w.startDate);
      } catch (e) {
        console.warn(`[setsDiscovered] onDerived side-write failed for ${w.uuid}`, e);
      }
    }
  }
  // The recount has now seen every swim the bridge will hand over, so a
  // migration snapshot has done its job: restore anything older than that
  // window and spend it. A no-op when no migration is in flight.
  return settleCarried(cache);
}

// ─── Task 9: stores + live wiring ────────────────────────────────────────────

export interface RecognitionCallout {
  setIds: string[];
  displayNames: string[];
}

/** Render-ready ledger, recency-sorted. Read by the sheet + History affordance. */
export const setsDiscoveredLedger = writable<LedgerRow[]>([]);

/** The one-time first-ever card for the current swim; null when nothing new. */
export const firstEverCallout = writable<RecognitionCallout | null>(null);

/**
 * Pure: fold the current swim into the cache and compute its (one-time)
 * recognition callout. Counting is idempotent (applySwimToCache); recognition
 * is gated separately via shownCallouts and marked here so re-processing the
 * same swim never re-fires the card.
 */
export function processCurrentSwim(
  cache: SetsDiscoveredCache,
  swim: AppliedSwim,
): { cache: SetsDiscoveredCache; callout: RecognitionCallout | null } {
  let next = applySwimToCache(cache, swim);
  // Bookends (Warmup/Cooldown) are not "sets" — never name them as a first-time
  // discovery in the callout (matches the ledger suppression above). Same for the
  // generic single-stroke sets (isSignature:false): we suppress them from the
  // ledger, so we must never fire a first-time recognition for them either.
  const ids = firstEverForShownSwim(next, swim.uuid).filter(
    (id) =>
      !BOOKEND_SET_IDS.has(id) && SET_DISPLAY[id]?.isSignature !== false,
  );
  if (ids.length === 0) return { cache: next, callout: null };
  next = markCalloutShown(next, swim.uuid);
  const displayNames = ids
    .map((id) => SET_DISPLAY[id]?.displayName)
    .filter((n): n is string => !!n);
  return { cache: next, callout: { setIds: ids, displayNames } };
}

/**
 * Reproduce the live path's setId derivation for the backfill scan, so the
 * scan and the view agree. `labels` are the swim's saved corrections: a fixed
 * stroke can make or break a signature set, so the ledger must count with them.
 */
export function deriveSetIdsFromFirehose(fh: SwimFirehoseResponse, labels: LapLabel[] = []): (string | null)[] {
  const dw = deriveWorkout(fh, labels);
  return dw.setGroups.map((g) => g.setId);
}

/**
 * Reconcile a finished history scan with a live cache that may have mutated
 * while the scan was in flight (the scan is seeded from an init-time snapshot
 * and never sees what the live firehose subscription writes concurrently).
 *
 * The scan is the counting-complete base for everything it saw. We fold it INTO
 * the live cache so live-only state survives:
 *   - `shownCallouts` — the scan never touches these; without a union, taking
 *     the scan result directly drops any first-ever callout the live path marked
 *     shown, and the recognition card RE-FIRES on the next re-publish. (load-
 *     bearing invariant: one first-ever callout per swim.)
 *   - live-only swims — any swim the live path folded but the scan did not see
 *     (e.g. a swim too new to appear in listWorkouts yet) is replayed on top of
 *     the scan via `liveApplied`, so its counts/entries are not lost.
 * Counting stays idempotent: applySwimToCache dedups by uuid, so a live swim the
 * scan also counted is a no-op on replay.
 */
export function mergeScanIntoLive(
  scanned: SetsDiscoveredCache,
  live: SetsDiscoveredCache,
  liveApplied: AppliedSwim[],
): SetsDiscoveredCache {
  // Start from the scan (counting-complete for history), then replay any
  // live-folded swim the scan's snapshot missed — idempotent by uuid.
  let merged: SetsDiscoveredCache = scanned;
  for (const swim of liveApplied) {
    merged = applySwimToCache(merged, swim);
  }
  // Union shownCallouts: neither path's recognition state may be dropped.
  const shownCallouts = [...new Set([...scanned.shownCallouts, ...live.shownCallouts])];
  return { ...merged, builtAt: Date.now(), shownCallouts };
}

let started = false;

/**
 * Mount the data engine. Idempotent (Prototypes may re-mount). Loads the
 * persisted cache, kicks off a non-blocking history backfill, and subscribes
 * to the live firehose so each new swim folds in and fires its first-ever card.
 */
export function initSetsDiscovered(): void {
  if (started) return; // idempotent — Prototypes may re-mount
  started = true;
  let cache = loadCache();
  setsDiscoveredLedger.set(toLedgerRows(cache));

  // Record every swim the live subscription folds while the scan is in flight,
  // so the post-scan merge can replay live-only swims the scan never saw.
  const liveApplied: AppliedSwim[] = [];

  // Background backfill (non-blocking). The scan is seeded from the init-time
  // snapshot, so it does NOT see anything the live subscription writes while it
  // runs. Merge the result INTO the (possibly live-mutated) closure `cache`
  // instead of overwriting it — see mergeScanIntoLive for what would otherwise
  // regress (dropped shownCallouts → re-firing recognition card; lost live-only
  // swim counts). The .catch keeps a thrown listWorkouts from becoming an
  // unhandled rejection.
  scanHistory({
    cache,
    listWorkouts: () => bridge.listWorkouts({ activityType: "swimming", limit: 100 }),
    getFirehose: (uuid) => bridge.getWorkoutFirehose({ workoutUUID: uuid }),
    setIdsFor: (fh, labels) => deriveSetIdsFromFirehose(fh, labels),
    labelsFor: (uuid) => loadSavedLabels(uuid),
    // Write path C: backfill the strokeStats.v2 + swimFacts.v1 caches from the
    // same scan — no second firehose fetch. Never writes setsDiscovered.
    onDerived: (derived, swimUuid, swimDate) => {
      recordStrokeStats(derived, swimUuid, swimDate);
      recordSwimFacts(derived, swimUuid, swimDate);
    },
    // Codex-Medium/Codex-High: retry a swim whose side-writes haven't landed
    // yet (failed before, or carried over from v1 without ever having run)
    // without re-counting it into the sets ledger. Requiring BOTH caches means
    // existing installs (which predate swimFacts.v1) refetch each historical
    // swim ONCE in the background to backfill the Scores tab's facts — the
    // designed retry path, not a tab-load re-derivation.
    hasDerived: (swimUuid) => hasStrokeStats(swimUuid) && hasSwimFacts(swimUuid),
  })
    .then((scanned) => {
      cache = mergeScanIntoLive(scanned, cache, liveApplied);
      saveCache(cache);
      setsDiscoveredLedger.set(toLedgerRows(cache));
    })
    .catch((e) => console.warn("[setsDiscovered] history scan failed", e));

  // Live: when a new swim's firehose lands, fold it in. raw is set AFTER derived
  // in Prototypes, so derived.setGroups is already current when this fires.
  currentRawFirehose.subscribe((raw) => {
    if (!raw) return;
    const derived = get(currentDerivedWorkout);
    if (!derived) return;
    const setIds = derived.setGroups.map((g) => g.setId);
    const appliedSwim: AppliedSwim = {
      uuid: raw.workout.uuid,
      startedAtMs: new Date(raw.workout.startDate).getTime(),
      setIds,
    };
    liveApplied.push(appliedSwim); // replayed by mergeScanIntoLive if scan still pending
    const res = processCurrentSwim(cache, appliedSwim);
    cache = res.cache;
    saveCache(cache);
    setsDiscoveredLedger.set(toLedgerRows(cache));
    firstEverCallout.set(res.callout);
  });
}
